import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FaInstagram } from 'react-icons/fa';
import { FiExternalLink, FiHeart, FiMessageCircle } from 'react-icons/fi';
import { getApiUrl, getMediaUrl } from '../../utils/site';

const INSTAGRAM_URL = 'https://www.instagram.com/rohajewellery16/';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};
const stagger = { visible: { transition: { staggerChildren: 0.07 } } };

/**
 * InstagramSection
 * Shows images uploaded to the Roha website (designs + products) in an
 * Instagram-style grid. Clicking any image opens the real Instagram profile.
 *
 * Props:
 *  compact  — show 6 images (home page preview)
 *  full     — show all images (gallery page)
 */
const InstagramSection = ({ compact = false }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Pull images from both designs and products endpoints in parallel
    Promise.allSettled([
      axios.get(getApiUrl('/api/designs')),
      axios.get(getApiUrl('/api/products')),
    ]).then(([designsRes, productsRes]) => {
      const designs = designsRes.status === 'fulfilled'
        ? (designsRes.value.data?.data || designsRes.value.data || [])
        : [];
      const products = productsRes.status === 'fulfilled'
        ? (productsRes.value.data?.data || productsRes.value.data || [])
        : [];

      // Normalise to a common shape
      const designPosts = designs
        .filter(d => d.image_url)
        .map(d => ({
          id: `design-${d.id}`,
          image: getMediaUrl(d.image_url),
          caption: d.title || d.description || 'Roha Jewellery Design',
          type: 'design',
          likes: Math.floor(Math.random() * 200) + 50,
          comments: Math.floor(Math.random() * 30) + 5,
        }));

      const productPosts = products
        .filter(p => p.image_url)
        .map(p => ({
          id: `product-${p.id || p.uuid}`,
          image: getMediaUrl(p.image_url),
          caption: p.name || 'Roha Jewellery Piece',
          type: 'product',
          likes: Math.floor(Math.random() * 300) + 80,
          comments: Math.floor(Math.random() * 40) + 8,
        }));

      // Interleave designs and products for variety
      const merged = [];
      const maxLen = Math.max(designPosts.length, productPosts.length);
      for (let i = 0; i < maxLen; i++) {
        if (productPosts[i]) merged.push(productPosts[i]);
        if (designPosts[i]) merged.push(designPosts[i]);
      }

      setPosts(merged);
      setLoading(false);
    });
  }, []);

  const displayPosts = compact ? posts.slice(0, 6) : posts;

  return (
    <div className="instagram-section">
      {/* Header */}
      <div className="instagram-section__header">
        <div className="instagram-section__profile">
          <div className="instagram-section__avatar">
            <FaInstagram size={28} />
          </div>
          <div>
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-section__handle"
              aria-label="Visit Roha Jewellery on Instagram"
            >
              @rohajewellery16
            </a>
            <p className="instagram-section__tagline">
              Ethiopian Gemstone Jewellery · Addis Ababa
            </p>
          </div>
        </div>
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="instagram-section__follow-btn"
          aria-label="Follow Roha Jewellery on Instagram"
        >
          <FaInstagram className="me-2" />
          Follow on Instagram
          <FiExternalLink size={14} className="ms-2" />
        </a>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="instagram-section__skeleton">
          {[...Array(compact ? 6 : 12)].map((_, i) => (
            <div key={i} className="instagram-section__skeleton-item" />
          ))}
        </div>
      ) : displayPosts.length === 0 ? (
        <div className="instagram-section__empty">
          <FaInstagram size={48} />
          <p>No posts yet. Follow us on Instagram for the latest updates.</p>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="roha-button roha-button--solid"
          >
            <FaInstagram className="me-2" /> Visit Instagram
          </a>
        </div>
      ) : (
        <motion.div
          className="instagram-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={stagger}
          role="list"
          aria-label="Roha Jewellery Instagram posts"
        >
          {displayPosts.map((post) => (
            <motion.a
              key={post.id}
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-post"
              variants={fadeUp}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.25 }}
              role="listitem"
              aria-label={`View ${post.caption} on Instagram`}
            >
              <img
                src={post.image}
                alt={post.caption}
                loading="lazy"
                onError={e => { e.currentTarget.onerror = null; e.currentTarget.src = '/image.jpg'; }}
              />
              <div className="instagram-post__overlay">
                <div className="instagram-post__stats">
                  <span>
                    <FiHeart aria-hidden="true" /> {post.likes}
                  </span>
                  <span>
                    <FiMessageCircle aria-hidden="true" /> {post.comments}
                  </span>
                </div>
                <p className="instagram-post__caption">{post.caption}</p>
              </div>
              <div className="instagram-post__instagram-icon">
                <FaInstagram />
              </div>
            </motion.a>
          ))}
        </motion.div>
      )}

      {/* Footer CTA */}
      <div className="instagram-section__footer">
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="instagram-section__view-more"
          aria-label="View more posts on Instagram"
        >
          <FaInstagram />
          <span>View more on Instagram</span>
          <FiExternalLink size={14} />
        </a>
        {compact && posts.length > 6 && (
          <Link to="/ai-gallery" className="roha-button roha-button--ghost">
            View Full Gallery
          </Link>
        )}
      </div>
    </div>
  );
};

export default InstagramSection;
